import { z } from 'zod';
import { uuidSchema } from './common.schema';

/* ------------------------------------------------------------------ */
/*  Enums                                                              */
/* ------------------------------------------------------------------ */

export const QuickFeedPrepStatusSchema = z.enum([
  'PENDING', 'PREPARED', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'VOIDED',
]);
export type QuickFeedPrepStatus = z.infer<typeof QuickFeedPrepStatusSchema>;

/** Route values — unified with FeedOrderRouteSchema. */
export const QuickFeedRouteSchema = z.enum([
  'SNG (Nasogastric)', 'VO (Oral/Per Os)', 'VGNS (Nasogastric)', 'OG (Orogastric)', 'PEG', 'Gavage',
]);
export type QuickFeedRoute = z.infer<typeof QuickFeedRouteSchema>;

/* ------------------------------------------------------------------ */
/*  POST /quickFeeds  —  Create a QuickFeed (directly in feed_preps)   */
/* ------------------------------------------------------------------ */

export const QuickFeedMilkSourceSchema = z.enum(['MOM', 'DONOR', 'FORMULA']);
export type QuickFeedMilkSource = z.infer<typeof QuickFeedMilkSourceSchema>;

export const CreateQuickFeedSchema = z.object({
  hospital_id:          uuidSchema,
  patient_id:           uuidSchema,
  route:                QuickFeedRouteSchema.optional(),
  prepared_by:          uuidSchema,
  milk_source_type:     QuickFeedMilkSourceSchema.optional(),  // auto-derived when receivable_id is provided
  milk_inventory_id:    uuidSchema.optional(),
  receivable_id:        uuidSchema.optional(),                 // raw receivable — volume/person_type validated server-side
  volume_ml:            z.number().positive().optional(),
  /** Container barcode entered by the user; backend uses it instead of auto-generating one. */
  container_identifier: z.string().optional(),
  /** ISO-8601 datetime for when the milk was pumped; becomes started_at in the DB. */
  pumped_date:          z.string().datetime({ offset: true }).optional(),
  /** Product ID of the formula being administered (FORMULA milk_source_type). Used for allergen checking. */
  formula_product_id:   uuidSchema.optional(),
});
export type CreateQuickFeed = z.infer<typeof CreateQuickFeedSchema>;

/* ------------------------------------------------------------------ */
/*  GET /quickFeeds/:prep_id  —  params                                */
/* ------------------------------------------------------------------ */

export const QuickFeedParamsSchema = z.object({
  prep_id: uuidSchema,
});
export type QuickFeedParams = z.infer<typeof QuickFeedParamsSchema>;

/* ------------------------------------------------------------------ */
/*  GET /quickFeeds?hospital_id=  —  list query                        */
/* ------------------------------------------------------------------ */

export const QuickFeedListQuerySchema = z.object({
  hospital_id:  uuidSchema,
  patient_id:   uuidSchema.optional(),
  status:       QuickFeedPrepStatusSchema.optional(),
  patient_name: z.string().min(1).max(100).optional(),
});
export type QuickFeedListQuery = z.infer<typeof QuickFeedListQuerySchema>;

/* ------------------------------------------------------------------ */
/*  Output — single feed_preparations row (prep_type = QUICK)          */
/* ------------------------------------------------------------------ */

export const QuickFeedSchema = z.object({
  prep_id:                uuidSchema,
  container_identifier:   z.string().nullable(),
  prep_type:              z.literal('QUICK'),
  patient_id:          uuidSchema.nullable(),
  route:               QuickFeedRouteSchema.nullable(),
  hospital_id:         uuidSchema,
  prepared_by:         uuidSchema,
  actual_volume_ml:    z.string().nullable(),
  status:              QuickFeedPrepStatusSchema,
  notes:               z.string().nullable(),
  started_at:          z.string(),
  created_at:          z.string(),
  updated_at:          z.string(),
});
export type QuickFeed = z.infer<typeof QuickFeedSchema>;

/* ------------------------------------------------------------------ */
/*  Detail — enriched with patient info                                */
/* ------------------------------------------------------------------ */

export const QuickFeedDetailSchema = QuickFeedSchema.extend({
  patient: z.object({
    first_name:  z.string(),
    middle_name: z.string().nullable(),
    last_name:   z.string(),
    mrn:         z.string(),
  }).nullable(),
});
export type QuickFeedDetail = z.infer<typeof QuickFeedDetailSchema>;

/* ------------------------------------------------------------------ */
/*  List item — lighter patient info                                   */
/* ------------------------------------------------------------------ */

export const QuickFeedListItemSchema = QuickFeedSchema.extend({
  patient: z.object({
    first_name: z.string(),
    last_name:  z.string(),
    mrn:        z.string(),
  }).nullable(),
  mother_name:  z.string().nullable(),
});
export type QuickFeedListItem = z.infer<typeof QuickFeedListItemSchema>;

/* ------------------------------------------------------------------ */
/*  Response for POST — includes delivery_id                           */
/* ------------------------------------------------------------------ */

export const CreateQuickFeedResponseSchema = QuickFeedSchema.extend({
  delivery_id:          uuidSchema.nullable().optional(),
  mother_name:          z.string().nullable().optional(),
  warnings:             z.array(z.string()).optional(),
});
export type CreateQuickFeedResponse = z.infer<typeof CreateQuickFeedResponseSchema>;

/* ------------------------------------------------------------------ */
/*  GET /quickFeeds/receivable/:receipt_number — resolve by barcode    */
/* ------------------------------------------------------------------ */

export const ReceivableLookupParamsSchema = z.object({
  receipt_number: z.string().min(1),
});
export type ReceivableLookupParams = z.infer<typeof ReceivableLookupParamsSchema>;

export const ReceivableLookupQuerySchema = z.object({
  hospital_id: uuidSchema,
});
export type ReceivableLookupQuery = z.infer<typeof ReceivableLookupQuerySchema>;

export const ReceivableLookupResponseSchema = z.object({
  receivable_id:        uuidSchema,
  receipt_number:       z.string(),
  volume_ml:            z.string(),
  /** Remaining available volume (from milk_inventory). Falls back to volume_ml when no inventory exists yet. */
  remaining_volume_ml:  z.string(),
  status:               z.string(),
  expiry_at:            z.string().nullable(),
  milk_source_type: z.enum(['MOM', 'DONOR', 'FORMULA']),
  provider: z.object({
    person_id:   uuidSchema,
    first_name:  z.string(),
    last_name:   z.string(),
    person_type: z.string(),
  }),
});
export type ReceivableLookupResponse = z.infer<typeof ReceivableLookupResponseSchema>;
